/**
 * The vault sidebar's TEAM sections — pure, so the rules are testable without
 * rendering a panel.
 *
 * The shape the owner asked for, mirroring the personal structure rather than
 * inventing a second one:
 *
 *     PERSONAL              <- only shown when a team section is also shown
 *       All / Favorites / Passwords / … / Custom
 *       Folders
 *     TEAM <name>           <- one section per team
 *       All / Passwords / … / Custom      (scoped to that team)
 *       Folders                            (that team's folders)
 *
 * FOUR RULES, each with a reason:
 *
 * 1. A TEAM SECTION IS HIDDEN WHEN IT HOLDS NOTHING THIS USER CAN SEE. Teams
 *    are a workspace-wide concept; vault grants are not. Most members of most
 *    teams hold no vault grant at all, and a permanently empty heading for
 *    every team they belong to is noise on the panel they use most.
 *    `entry.items` is already the server's authorized view, so "nothing to
 *    show" is exactly `items.length === 0`.
 *
 * 2. NO FAVOURITES ROW FOR A TEAM. `favorite` is a personal-vault column; team
 *    items have no such field on the wire. A row that could only ever read 0
 *    would be a promise the feature does not keep.
 *
 * 3. EVERY TEAM FOLDER APPEARS, INCLUDING AN EMPTY ONE. Reversed 06.08.2026.
 *
 *    It used to hide folders holding nothing, copying the personal list — but
 *    the two are not the same thing, and the difference is the whole reason
 *    this rule was wrong. A PERSONAL folder is DERIVED: it exists exactly as
 *    long as an item carries its name, so "empty" and "absent" really are one
 *    state (and where they are not, `__folders__` was added precisely so an
 *    empty one could still be shown). A TEAM folder is a real server row with
 *    its own id and its own grants. It exists whether or not anything is in it.
 *
 *    Hiding them made "New folder" look broken: the POST succeeded, the folder
 *    was created, and the sidebar showed nothing — no error, no row, nothing to
 *    act on. The owner created SEVEN folders that way before reporting it, and
 *    every one of them was on the server the whole time.
 *
 *    It also made the first folder in a team unreachable: you cannot file an
 *    item into a folder you cannot see, so a folder could never stop being
 *    empty.
 *
 *    `entry.folders` is the SERVER's authorized list, so this discloses
 *    nothing — a folder-scoped grantee still sees only their own. The list is
 *    filtered by permission, not by content.
 *
 * 4. CATEGORY IDS ARE NOT ITEM IDS, AND CANNOT BE MISTAKEN FOR ONE. They are
 *    prefixed `teamcat:`, deliberately NOT `team:` — a `team:<team>:<item>` key
 *    is an ITEM id that both `teamVaultService.parseTeamItemKey` and Rust's
 *    `team_vault::parse_team_item_key` will happily parse. A sidebar selection
 *    that could be parsed as a credential reference is the kind of coincidence
 *    that becomes a bug once something starts routing on it.
 *
 * Nothing here imports a store: it takes `byTeam` as data. That keeps it free
 * of the import cycle `vaultStore` would otherwise land in, and it is why the
 * whole file can be tested with object literals.
 */

/** Prefix for every team sidebar selection. Never `team:` — see rule 4. */
export const TEAM_CATEGORY_PREFIX = 'teamcat:';

/**
 * The type rows a team section shows, in the personal sidebar's own order so
 * the two read as one list. `favorites` is absent by rule 2; `all` leads, as it
 * does in the personal list.
 */
export const TEAM_TYPE_CATEGORIES = [
  'password',
  'sftp',
  'dbConnection',
  'authHeader',
  'note',
  'custom',
];

/** The team-root pseudo-folder: `folder_id === null` server-side. */
export const TEAM_ROOT_FOLDER = 'root';

const str = (v) => String(v);

/** `teamcat:<teamId>:all` — everything in this team the user may read. */
export function teamAllCategory(teamId) {
  return `${TEAM_CATEGORY_PREFIX}${str(teamId)}:all`;
}

/** `teamcat:<teamId>:type:<itemType>`. */
export function teamTypeCategory(teamId, itemType) {
  return `${TEAM_CATEGORY_PREFIX}${str(teamId)}:type:${itemType}`;
}

/** `teamcat:<teamId>:folder:<folderId | 'root'>`. */
export function teamFolderCategory(teamId, folderId) {
  return `${TEAM_CATEGORY_PREFIX}${str(teamId)}:folder:${str(folderId)}`;
}

/**
 * `{ teamId, kind, value }` for a team category, or `null` for anything else —
 * including every personal category and every team ITEM id.
 *
 * `kind` is `'all' | 'type' | 'folder'`; `value` is the type name, the folder
 * id, or `'root'`.
 */
export function parseTeamCategory(id) {
  if (typeof id !== 'string' || !id.startsWith(TEAM_CATEGORY_PREFIX)) return null;
  const rest = id.slice(TEAM_CATEGORY_PREFIX.length);
  const firstSep = rest.indexOf(':');
  if (firstSep <= 0) return null;
  const teamId = rest.slice(0, firstSep);
  const tail = rest.slice(firstSep + 1);
  if (tail === 'all') return { teamId, kind: 'all', value: null };
  const secondSep = tail.indexOf(':');
  if (secondSep <= 0 || secondSep === tail.length - 1) return null;
  const kind = tail.slice(0, secondSep);
  const value = tail.slice(secondSep + 1);
  if (kind !== 'type' && kind !== 'folder') return null;
  return { teamId, kind, value };
}

/** True for a sidebar selection that belongs to a team, false for personal. */
export function isTeamCategory(id) {
  return parseTeamCategory(id) != null;
}

/** Per-type counts for one team's authorized item list, plus `all`. */
export function teamCategoryCounts(items) {
  const list = Array.isArray(items) ? items : [];
  const counts = { all: list.length };
  for (const type of TEAM_TYPE_CATEGORIES) counts[type] = 0;
  for (const it of list) {
    const type = it?.item_type;
    if (counts[type] != null) counts[type] += 1;
  }
  return counts;
}

/** Item counts keyed by folder id, with `root` for the team root. */
export function teamFolderCounts(items) {
  const counts = {};
  for (const it of Array.isArray(items) ? items : []) {
    const key = it?.folder_id == null ? TEAM_ROOT_FOLDER : str(it.folder_id);
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

/**
 * One section per team with something to show, in the order `teams` gives.
 *
 * Rule 1 lives here: a team whose authorized item list is empty produces no
 * section at all, so a user who belongs to five teams and holds a grant in one
 * sees exactly one team heading. A user in no team gets `[]`, which is what
 * keeps the solo sidebar byte-for-byte what it was.
 */
export function buildTeamSections({ teams, byTeam }) {
  const map = byTeam && typeof byTeam === 'object' ? byTeam : {};
  const sections = [];
  for (const team of Array.isArray(teams) ? teams : []) {
    if (!team || team.id == null) continue;
    const entry = map[str(team.id)];
    // NOT LOADED is not the same as EMPTY. Without this, a team whose fetch is
    // still in flight would render "nothing has been shared with you" and then
    // silently change its mind a moment later — worse than showing nothing,
    // because the first message tells the user to go ask an admin.
    if (!entry) continue;
    const items = Array.isArray(entry?.items) ? entry.items : [];
    // A team is ALWAYS shown, even with nothing in it — reversed after the
    // owner used the hidden version. A team that vanishes reads as broken, and
    // hiding it gives no way to tell "nobody has added anything" apart from
    // "nothing has been shared with you". Those are different problems with
    // different fixes: wait, versus go ask an admin.
    const grants = Array.isArray(entry?.grants) ? entry.grants : [];
    const emptyReason =
      items.length > 0 ? null : grants.length > 0 ? 'empty' : 'no-access';
    const folderCounts = teamFolderCounts(items);
    // EMPTY TEAM FOLDERS ARE SHOWN — rule 3 no longer applies here; see its
    // entry in the header for why it was reversed. `entry.folders` is already
    // the SERVER's authorized list, so a folder-scoped grantee still sees only
    // the folders they hold a grant on. Nothing is disclosed by showing an
    // empty one; it is filtered by permission, not by content.
    const folders = (Array.isArray(entry?.folders) ? entry.folders : [])
      .filter((f) => f && f.id != null)
      .map((f) => ({ id: str(f.id), name: f.name || '', count: folderCounts[str(f.id)] || 0 }));
    sections.push({
      teamId: str(team.id),
      teamName: team.name || '',
      counts: teamCategoryCounts(items),
      folders,
      // The root row is a real place holding items, not "no folder" — but it
      // is only worth a row when something is actually in it (rule 3).
      rootCount: folderCounts[TEAM_ROOT_FOLDER] || 0,
      total: items.length,
      // null when the team has items; otherwise WHY it is empty, so the sidebar
      // can say something useful instead of showing a bare heading.
      emptyReason,
    });
  }
  return sections;
}

/**
 * Does the sidebar draw section headings at all?
 *
 * "PERSONAL" is a heading only in contrast to a team heading. On a vault with
 * one section it is a label on the only thing there — noise — so the solo user
 * keeps exactly the sidebar they had.
 */
export function shouldShowSectionHeadings(sections) {
  return Array.isArray(sections) && sections.length > 0;
}

/** Name + every plaintext index value, the same haystack the personal list uses. */
function matchesQuery(item, q) {
  if (!q) return true;
  const haystack = [item?.name, ...Object.values(item?.index || {})]
    .filter((v) => typeof v === 'string' || typeof v === 'number')
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

/**
 * One team's items narrowed by a team category + the search box.
 *
 * Returns `[]` for a category belonging to a DIFFERENT team, which is what
 * stops a stale selection from painting another team's inventory during the
 * render between switching teams and the store catching up.
 */
export function filterTeamItems(items, category, search) {
  const parsed = parseTeamCategory(category);
  if (!parsed) return [];
  const q = String(search || '').trim().toLowerCase();
  return (Array.isArray(items) ? items : []).filter((it) => {
    if (!it) return false;
    if (parsed.kind === 'type' && it.item_type !== parsed.value) return false;
    if (parsed.kind === 'folder') {
      const folder = it.folder_id == null ? TEAM_ROOT_FOLDER : str(it.folder_id);
      if (folder !== parsed.value) return false;
    }
    return matchesQuery(it, q);
  });
}
